import React, { useCallback, useContext, useEffect, useState } from 'react';

import { t } from 'i18next';

import CoverageGraph from './sections/CoverageGraph/CoverageGraph';
import ProtectionActionsCard from './sections/ProtectionActions/ProtectionActionsCard';
import MetricsOnboardingCard from '../../../../shared/components/v3/Cards/MetricsOnboardingCard/MetricsOnboardingCard';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { MetricsContext } from '../../../../shared/context/metrics/metricsContext';
import { IActionCard } from '../../../../shared/interfaces/actionCard';
import type {
  ILabelData,
  IMetricsProtectionByCases,
  IMetricsProtectionCoverage,
  IMetricsProtectionInsurance,
  IStackedCircleChartData
} from '../../../../shared/interfaces/metrics';
import MetricsByCompany from '../../../../shared/services/api/metrics/metricsByCompany/metrics';
import { TRANSLATIONS_METRICS_NE } from '../../../../shared/translations/metrics';
import { ProtectionType } from '../../../../shared/types/metrics';
import { toLocaleNumberFormat } from '../../../../shared/utils/utils';
import { isEmptyObject } from '../../../../shared/utils/validation/validation';

interface IProtectionMetrics {
  byCasesData: IMetricsProtectionByCases;
  loading: boolean;
  error: boolean;
}
const ProtectionMetrics = ({ byCasesData, loading, error }: IProtectionMetrics) => {
  const [loadingCoverage, setLoadingCoverage] = useState<boolean>(false);
  const [loadingInsurance, setLoadingInsurance] = useState<boolean>(false);
  const [errorInsurance, setErrorInsurance] = useState<boolean>(false);
  const [errorCoverage, setErrorCoverage] = useState<boolean>(false);
  const [modality, setModality] = useState<ProtectionType | undefined>(undefined);
  const [coverageData, setCoverageData] = useState<IMetricsProtectionCoverage>();
  const [insuranceData, setInsuranceData] = useState<IMetricsProtectionInsurance>();

  const { company } = useContext(CompanyContext);
  const { periodSelection } = useContext(MetricsContext);

  const isFirstCase = modality === 'dynamic';

  useEffect(() => {
    if (!isEmptyObject(byCasesData) && byCasesData.case) {
      setModality(byCasesData.case);
    }
  }, [byCasesData]);

  const getCoverage = useCallback(async () => {
    setLoadingCoverage(true);
    setErrorCoverage(false);
    try {
      const response = await MetricsByCompany.getProtectionCoverageMetrics(
        company.id,
        periodSelection.period,
        periodSelection.month
      );
      if (isEmptyObject(response)) {
        setCoverageData(undefined);
      } else {
        setCoverageData(response);
      }
    } catch {
      setErrorCoverage(true);
    } finally {
      setLoadingCoverage(false);
    }
  }, [company.id, periodSelection]);

  const getInsuranceProtection = useCallback(async () => {
    setLoadingInsurance(true);
    setErrorInsurance(false);
    try {
      const response = await MetricsByCompany.getInsuranceProtection(
        company.id,
        periodSelection.period,
        periodSelection.month
      );
      setInsuranceData(response);
    } catch {
      setLoadingInsurance(true);
      setErrorInsurance(true);
    } finally {
      setLoadingInsurance(false);
    }
  }, [company.id, periodSelection]);

  useEffect(() => {
    getCoverage();
  }, [getCoverage]);

  useEffect(() => {
    getInsuranceProtection();
  }, [getInsuranceProtection]);

  const renderStackedCircles = () => {
    let labelsData: ILabelData[] = [];
    let stackedCircleChartData: IStackedCircleChartData[] = [];

    if (byCasesData) {
      const totalEmployes = byCasesData.totalMembers ?? 0;
      const totalInsuredMembers = byCasesData.totalInsuredMembers ?? 0;

      labelsData = [
        {
          label: t(TRANSLATIONS_METRICS_NE.protections.chartDescription),
          value: Number(byCasesData.percentageInsured)
        }
      ];

      stackedCircleChartData = [
        {
          label: t(TRANSLATIONS_METRICS_NE.protections.labels.totalEmployes, { count: totalEmployes }),
          tooltip: t(TRANSLATIONS_METRICS_NE.protections.tooltipWithoutInsurance.invited, {
            users: totalEmployes,
            count: totalEmployes
          }),
          value: totalEmployes
        },
        {
          label: t(TRANSLATIONS_METRICS_NE.protections.labels.insuredEmployes, { count: totalInsuredMembers }),
          tooltip: t(TRANSLATIONS_METRICS_NE.protections.tooltipWithoutInsurance.active, {
            users: totalInsuredMembers,
            count: totalInsuredMembers
          }),
          value: totalInsuredMembers
        }
      ];
    }

    return (
      <MetricsOnboardingCard
        timelineChartData={[]}
        isPercentage={true}
        data={labelsData}
        stackedCircleChartData={stackedCircleChartData}
        loading={loading}
        error={error}
        colorScheme="protection"
        title={t(renderTitle(false), { count: 2 })}
      />
    );
  };

  const renderCardsAction = () => {
    let data: IActionCard[] = [];
    let title = '';

    if (byCasesData) {
      title = renderTitle(true);

      const cardFirstValue = isFirstCase ? byCasesData.initialCoverageGrowth : byCasesData.averageCoveragePerMember;
      const cardSecondValue = isFirstCase ? byCasesData?.teamCoverageGrowth : insuranceData?.useIntentionRate;

      data = [
        {
          value: renderValue(cardFirstValue ?? 0, isFirstCase ? undefined : byCasesData.currency),
          description: renderDescriptionCard()[0],
          insight: periodSelection.month && renderInsight(byCasesData)[0]
        },
        {
          value: renderValue(cardSecondValue ?? 0),
          description: renderDescriptionCard()[1],
          insight: periodSelection.month && renderInsight(byCasesData)[1]
        }
      ];
    }

    return (
      <ProtectionActionsCard
        loading={loading ?? loadingInsurance}
        title={title}
        error={error ?? errorInsurance}
        data={data}
      />
    );
  };

  const renderInsight = (data: IMetricsProtectionByCases): { [key: number]: string } => {
    const value = isFirstCase ? `${data.teamCoverageGrowthDiff ?? 0}%` : `${insuranceData?.insight ?? 0}%`;

    if (isFirstCase) {
      return {
        0: t(TRANSLATIONS_METRICS_NE.protections.cards.insight, { value: `${data.initialCoverageGrowthDiff ?? 0}%` }),
        1: t(TRANSLATIONS_METRICS_NE.protections.cards.insight, { value: value })
      };
    } else {
      return {
        1: t(TRANSLATIONS_METRICS_NE.protections.cards.insight, { value: value })
      };
    }
  };

  const renderTitle = (isCard?: boolean): string => {
    if (isCard) {
      return isFirstCase
        ? TRANSLATIONS_METRICS_NE.protections.cards.withInsuranceTitle
        : TRANSLATIONS_METRICS_NE.protections.cards.withoutInsuranceTitle;
    } else {
      return TRANSLATIONS_METRICS_NE.protections.titleWithoutInsurance;
    }
  };

  const renderValue = (value: number, currency?: string): string => {
    const valueFormated = value.toString().replaceAll('.', ',');

    if (currency) {
      return `${toLocaleNumberFormat(Number(value))} ${currency}`;
    } else {
      return `${valueFormated}%`;
    }
  };

  const renderDescriptionCard = (): { [key: number]: string } =>
    modality === 'dynamic'
      ? {
          0: TRANSLATIONS_METRICS_NE.protections.cards.withInsuranceDescription1,
          1: TRANSLATIONS_METRICS_NE.protections.cards.withInsuranceDescription2
        }
      : {
          0: TRANSLATIONS_METRICS_NE.protections.cards.withoutInsuranceDescription1,
          1: TRANSLATIONS_METRICS_NE.protections.cards.withoutInsuranceDescription2
        };

  const renderGraph = useCallback(() => {
    if (modality != 'fixed') {
      return (
        <div className="third-component">
          <CoverageGraph
            fetch={getCoverage}
            coverageData={coverageData}
            isError={errorCoverage}
            isLoading={loadingCoverage}
          />
        </div>
      );
    }
  }, [coverageData, errorCoverage, getCoverage, loadingCoverage, modality]);

  return (
    <div className="metrics-content-section-protection">
      <div className="first-component">{renderStackedCircles()}</div>
      <div className="second-component">{renderCardsAction()}</div>
      {renderGraph()}
    </div>
  );
};

export default ProtectionMetrics;
